import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { RecordingOpts } from "@/components/HomeFeed";
import type { CollectionCardData } from "./types";
import CollectionDetailSheet from "@/components/CollectionDetailSheet";
import {
  CARD_PADDING,
  CARD_RADIUS,
  labelStyle,
  titleStyle,
  bodyStyle,
} from "./types";

interface CollectionCardProps {
  collection: CollectionCardData;
  onRecord: (opts?: RecordingOpts) => void;
}

const CollectionCard = ({ collection, onRecord }: CollectionCardProps) => {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);

  const relativeTime = (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days < 1) return t("collections.relativeTime.today");
    if (days === 1) return t("collections.relativeTime.yesterday");
    if (days < 7) return t("collections.relativeTime.daysAgo", { count: days });
    if (days < 30) return t("collections.relativeTime.weeksAgo", { count: Math.floor(days / 7) });
    const months = Math.floor(days / 30);
    if (months < 12) return t("collections.relativeTime.monthsAgo", { count: months });
    return t("collections.relativeTime.yearsAgo", { count: Math.floor(months / 12) });
  };

  const bgWithOpacity = `${collection.collectionColor}1A`; // 10% opacity
  const borderColor = `${collection.collectionColor}50`; // 30% opacity

  return (
    <>
      <div
        style={{
          backgroundColor: bgWithOpacity,
          borderLeft: `3px solid ${collection.collectionColor}`,
          border: `1px solid ${borderColor}`,
          borderLeftWidth: 3,
          borderLeftColor: collection.collectionColor,
          padding: CARD_PADDING,
          borderRadius: CARD_RADIUS,
          height: "calc(50dvh - 60px)",
          minHeight: 240,
          maxHeight: 320,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={labelStyle("#6B5744")}>Collection</div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <span style={{ fontSize: 28 }}>{collection.collectionIcon}</span>
            <span style={{ ...titleStyle("#1A0F00"), fontSize: 16 }}>
              {collection.collectionName}
            </span>
          </div>

          <p style={{ ...bodyStyle("#6B5744"), marginTop: 8 }}>
            {collection.lastEntryAt
              ? `${t("collections.entry", { count: collection.entryCount })} · ${t("collections.lastEntry", { time: relativeTime(collection.lastEntryAt) })}`
              : t("collections.noEntriesYet")}
          </p>

          <button
            onClick={() => setDetailOpen(true)}
            style={{ ...bodyStyle(collection.collectionColor), marginTop: 4, fontSize: 12, textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            {t("collections.seeAllEntries", { count: collection.entryCount })}
          </button>
        </div>

        <div style={{ paddingTop: 12 }}>
          <button
            type="button"
            onClick={() =>
              onRecord({
                contextPrompt: collection.collectionName,
                collectionId: collection.collectionId,
                collectionParticipantConnectionIds: collection.participantConnectionIds,
              })
            }
            style={{
              backgroundColor: collection.collectionColor,
              color: "#FFFFFF",
              fontSize: 14,
              fontWeight: 500,
              padding: "10px 0",
              borderRadius: 24,
              border: "none",
              cursor: "pointer",
              width: "100%",
              textAlign: "center",
              display: "block",
            }}
          >
            Add another entry →
          </button>
        </div>
      </div>

      <CollectionDetailSheet
        collectionId={collection.collectionId}
        collectionName={collection.collectionName}
        collectionIcon={collection.collectionIcon}
        collectionColor={collection.collectionColor}
        entryCount={collection.entryCount}
        templateId={null}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onRecord={onRecord}
        cardContext={collection.cardContext}
        collectionParticipantConnectionIds={collection.participantConnectionIds}
        collectionParticipantChildIds={collection.participantChildIds}
        lockedChildIds={collection.lockedChildIds}
        lockedMemberIds={collection.lockedMemberIds}
      />
    </>
  );
};

export default CollectionCard;
